import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TitleCard from "../Cards/TitleCard";
import { loadProblem, loadProblemMessage, requestPath } from "./useLoad";

/**
 * A load that didn't succeed, said as such (owner, 14-15 Oct 2026: a panel says what it knows - after a failed load,
 * that it failed). Never the empty state, never "Loading" for ever. The server's own words when it gave any, and a way
 * to try again.
 *
 * Takes a load from useLoad (`load`), or the parts of one (`problem`, `error`) from a screen that loads by hand.
 * `compact`: a line inside a panel or beside a field, where the rest of the page is still there; otherwise a card in
 * place of the page. `title` / `titles` / `bodies` give the screen's own wording - literal keys at the call site.
 *
 * Tagged data-load-state="failed" and data-load-failed="<request path>": the refusing-server guard checks that every
 * request the server refused is said on screen by one of these.
 */
function LoadFailure({ load, problem: givenProblem, error: givenError, onRetry, compact = false, title, titles = {},
                       bodies = {}, backTo = null, backLabel }) {
  const { t } = useTranslation();
  const error = load ? load.error : givenError;
  const problem = load ? load.problem : givenProblem || loadProblem(givenError);
  const retry = onRetry || load?.reload;
  const heading = title ?? titles[problem] ?? (problem === "REFUSED" ? t("loadRefusedTitle")
    : problem === "NOT_FOUND" ? t("loadNotFoundTitle") : t("loadFailedTitle"));
  const body = problem in bodies ? bodies[problem] : null;
  const said = loadProblemMessage(error);
  const tags = { "data-load-state": "failed", "data-load-failed": (load ? load.request : requestPath(error)) || "" };

  if (compact) {
    return (
      <div role="alert" className="text-sm text-error flex flex-wrap items-center gap-2" {...tags}>
        <span>{heading}</span>
        {body && <span>{body}</span>}
        {said && <span className="opacity-80">{t("loadServerSaid", { message: said })}</span>}
        {problem === "FAILED" && retry && (
          <button type="button" className="btn btn-ghost btn-xs" onClick={retry}>{t("loadTryAgain")}</button>
        )}
      </div>
    );
  }
  return (
    <TitleCard title={heading} topMargin="mt-2">
      <div role="alert" {...tags}>
        {body && <p>{body}</p>}
        {said && <p className="mt-2 text-sm">{t("loadServerSaid", { message: said })}</p>}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {problem === "FAILED" && retry && (
          <button type="button" className="btn btn-primary min-h-12" onClick={retry}>{t("loadTryAgain")}</button>
        )}
        {backTo && <Link className="btn btn-outline min-h-12" to={backTo}>{backLabel || t("loadBack")}</Link>}
      </div>
    </TitleCard>
  );
}

export default LoadFailure;
